import { useMoveQuotationTaskPhaseToNext } from '@/hooks/api/1490_move_to_next_phase';
import { useMoveQuotationTaskPhaseToPrev } from '@/hooks/api/1491_move_to_prev_phase';
import { useCurrentQuotationTask } from '@/providers/QuotationTaskProvider';
import { useQuotationTaskPhases } from '@/hooks/api/1430_quotation_task_phases';
import { Fragment, RefObject, useEffect, useRef, useState } from 'react';
import { QuotationTaskPhase } from '@/hooks/api/models';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { toastApiError } from '@/lib/toast';
import { MotionDiv } from '@/components/element/MotionDiv';
import { useQuotationTasksInvalidate } from '@/hooks/api/1440_quotation_tasks';
import { useQuotationTaskInvalidate } from '@/hooks/api/1480_quotation_task';
import { useParams } from 'react-router-dom';
import { ModalMessage } from '@/components/element/modal/ModalMessage';

export const TaskProgress = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { data: phases } = useQuotationTaskPhases();
  const [currentPhase, setCurrentPhase] = useState(task!.quotation_task_phase);
  const { invalidate: invalidateTasks } = useQuotationTasksInvalidate(_w_!.identifier);
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setCurrentPhase(task!.quotation_task_phase);
    invalidateTasks();
  }, [task]);

  if (phases == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div>
          <h6 className="mb-0">タスク進捗状況</h6>
        </div>
      </div>
      <div className="card-body pt-0">
        <ModalMessage
          modalId="messageModal"
          message="プロジェクトの基本情報で、適切なフェーズに変更してください"
          messageLink={{
            url: `/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${task!.sales_order.id}`,
            label: `${task!.sales_order.name}`,
          }}
          showModalButtonRef={showMessageButtonRef}
        />

        {phases.map((phase, i) => (
          <Fragment key={`qtp-${phase.id}`}>
            {i != 0 && <hr className="horizontal dark" />}
            <div className="d-flex align-items-center justify-content-between">
              <div className="text-sm text-dark font-weight-bold">{phase.label}</div>
              <div className="d-flex justify-content-end align-items-center">
                {phase.id == currentPhase.id && (
                  <img src={task!.user.user_account.icon_file_url} className="avatar avatar-sm rounded-circle" />
                )}
                {phase.id < currentPhase.id && <span className="badge badge-success">OK</span>}
                <MoveToPrevPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
                <MoveToNextPhaseButton
                  phase={phase}
                  curPhase={currentPhase}
                  phases={phases}
                  showMessageButtonRef={showMessageButtonRef}
                />
              </div>
            </div>
          </Fragment>
        ))}
      </div>
    </MotionDiv>
  );
};

type ButtonProps = {
  phase: QuotationTaskPhase;
  curPhase: QuotationTaskPhase;
  phases: QuotationTaskPhase[];
  showMessageButtonRef: RefObject<HTMLButtonElement>;
};

// 次のフェーズボタン
const MoveToNextPhaseButton = ({ phase, curPhase, phases, showMessageButtonRef }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveQuotationTaskPhaseToNext();
  const { invalidate: invalidateTask } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) {
      invalidateTask();

      if (data.quotation_task_phase.name == 'approved' || data.quotation_task_phase.name == 'completed') {
        showMessageButtonRef.current?.click();
      }
    }
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_quotation_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases.at(-1)!.id) return <></>;

  return (
    <button
      className={`btn btn-icon-only bg-gradient-success ms-1 ${
        phase.name == 'request_approval' && !isApprover ? 'disabled' : ''
      }`}
      onClick={onClick}
    >
      <i className="fas fa-arrow-down"></i>
    </button>
  );
};

// 前のフェーズボタン
const MoveToPrevPhaseButton = ({ phase, curPhase, phases }: ButtonProps) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, isApprover } = useCurrentTeamMember();
  const { sales_order_id } = useParams();
  const { quotationTask: task } = useCurrentQuotationTask();
  const { mutate, data, isPending, isSuccess, error } = useMoveQuotationTaskPhaseToPrev();
  const { invalidate: invalidateTask } = useQuotationTaskInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    sales_order_id: Number(sales_order_id),
    id: task!.id,
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  useEffect(() => {
    if (isSuccess && data) invalidateTask();
  }, [isSuccess]);

  const onClick = () => {
    mutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      sales_order_id: Number(sales_order_id),
      task_id: task!.id,
      current_quotation_task_phase_id: curPhase.id,
    });
  };

  if (phase.id != curPhase.id) return <></>;
  if (phase.id == phases[0].id) return <></>;

  return (
    <button className="btn btn-icon-only bg-gradient-warning ms-1" onClick={onClick}>
      <i className="fas fa-arrow-up"></i>
    </button>
  );
};
